<template>
  <ResilientAvatarImage
    class="unit-avatar"
    :class="[
      `unit-avatar--${size}`,
      {
        'unit-avatar--anonymous': isAnonymous,
        'unit-avatar--emphasized': emphasis,
      },
    ]"
    :data-avatar-size="size"
    :src="isAnonymous ? '' : photoUrl"
    :alt="accessibleAlt"
    @load="$emit('load', $event)"
    @error="$emit('error', $event)"
  >
    <template #fallback>
      <span
        class="unit-avatar__fallback"
        :aria-hidden="decorative ? 'true' : undefined"
      >
        {{ fallbackInitial }}
      </span>
    </template>
  </ResilientAvatarImage>
</template>

<script>
import { computed } from "vue";
import ResilientAvatarImage from "@/components/UserComponents/ResilientAvatarImage.vue";
import {
  getAvatarInitial,
  isAnonymousAvatarUser,
  resolveAvatarName,
  resolveAvatarSource,
} from "@/components/UserComponents/avatarUtils";
import { useAvatarUser } from "@/components/UserComponents/useAvatarUser";

const AVATAR_SIZES = ["tiny", "small", "medium", "large"];

export default {
  name: "Avatar",
  components: {
    ResilientAvatarImage,
  },
  props: {
    user: {
      type: [Object, Array],
      default: null,
    },
    userUid: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) => AVATAR_SIZES.includes(value),
    },
    emphasis: {
      type: Boolean,
      default: false,
    },
    decorative: {
      type: Boolean,
      default: false,
    },
    alt: {
      type: String,
      default: "",
    },
  },
  emits: ["load", "error"],
  setup(props) {
    const resolvedUser = useAvatarUser(props);
    const isAnonymous = computed(() =>
      isAnonymousAvatarUser(resolvedUser.value)
    );
    const photoUrl = computed(() =>
      resolveAvatarSource(
        resolvedUser.value,
        props.size === "large" ? "md" : "sm"
      )
    );
    const displayName = computed(() =>
      resolveAvatarName(resolvedUser.value)
    );
    const accessibleAlt = computed(() => {
      if (props.decorative) return "";
      return props.alt.trim() || displayName.value;
    });
    const fallbackInitial = computed(() =>
      getAvatarInitial(resolvedUser.value)
    );

    return {
      accessibleAlt,
      fallbackInitial,
      isAnonymous,
      photoUrl,
    };
  },
};
</script>

<style scoped>
.unit-avatar {
  flex: 0 0 auto;
  inline-size: var(--unit-avatar-size);
  block-size: var(--unit-avatar-size);
  border: 1px solid transparent;
  border-radius: 50%;
  box-sizing: border-box;
  overflow: hidden;
  background: var(--color-surface-subtle);
  color: var(--color-text-primary);
  text-transform: uppercase;
}

.unit-avatar--tiny {
  --unit-avatar-size: calc(var(--space-5) * 3);
  font-size: var(--font-size-caption);
  border-color: var(--color-text-primary);
}

.unit-avatar--small {
  --unit-avatar-size: var(--control-height-compact);
  font-size: var(--font-size-body);
  border-color: var(--color-text-secondary);
}

.unit-avatar--medium {
  --unit-avatar-size: var(--control-height-default);
  font-size: var(--font-size-title);
}

.unit-avatar--large {
  --unit-avatar-size: calc(var(--control-height-default) * 1.6);
  background: var(--color-text-secondary);
  color: var(--color-text-on-media);
  font-size: var(--font-size-heading);
}

.unit-avatar--anonymous {
  background: var(--color-surface-subtle);
  color: var(--color-text-secondary);
}

.unit-avatar--emphasized {
  border-color: var(--color-accent-primary);
  color: var(--color-accent-primary);
}

.unit-avatar__fallback {
  display: flex;
  inline-size: 100%;
  block-size: 100%;
  align-items: center;
  justify-content: center;
  font: inherit;
  font-weight: var(--font-weight-medium);
  line-height: 1;
}
</style>
